import { useId, useRef, useState } from 'react';
import { UploadIcon } from 'lucide-react';
import { cn } from '../cn';

export interface DropzoneProps {
  /** Called with the file the person dropped or chose. */
  readonly onFile: (file: File) => void;
  /** The `accept` list for the file dialog, e.g. `image/*,application/pdf`. */
  readonly accept?: string;
  /** The one-line invitation, e.g. "Drop the floor plan here". */
  readonly label: string;
  /** What is accepted, in words, e.g. "An image or a PDF". */
  readonly hint?: string;
  readonly disabled?: boolean;
  /** Shown instead of the invitation while an upload is in flight. */
  readonly uploading?: boolean;
  readonly className?: string;
}

/**
 * A place to drop a file, or click to choose one. Highlights while a file is
 * dragged over it, reads as a button to a keyboard and a screen reader, and
 * says what it will accept — so nobody drops a spreadsheet on a floor-plan
 * box and wonders why nothing happened.
 *
 * @param props - The file handler, what to accept, and the copy.
 * @returns The dropzone.
 */
export function Dropzone({
  onFile,
  accept,
  label,
  hint,
  disabled = false,
  uploading = false,
  className,
}: DropzoneProps): React.ReactElement {
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  const inert = disabled || uploading;

  const take = (files: FileList | null): void => {
    const file = files?.[0];
    if (file && !inert) onFile(file);
  };

  return (
    <div
      role="button"
      tabIndex={inert ? -1 : 0}
      aria-disabled={inert}
      aria-describedby={hint ? `${inputId}-hint` : undefined}
      onClick={() => {
        if (!inert) inputRef.current?.click();
      }}
      onKeyDown={(event) => {
        if (inert) return;
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          inputRef.current?.click();
        }
      }}
      onDragOver={(event) => {
        event.preventDefault();
        if (!inert) setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(event) => {
        event.preventDefault();
        setOver(false);
        take(event.dataTransfer.files);
      }}
      className={cn(
        'flex cursor-pointer flex-col items-center justify-center gap-2 rounded-portal-card border-2 border-dashed px-6 py-10 text-center transition-colors',
        over
          ? 'border-brand bg-brand-tint'
          : 'border-portal-line bg-portal-sky/40 hover:border-brand hover:bg-portal-sky',
        inert && 'cursor-default opacity-60 hover:border-portal-line hover:bg-portal-sky/40',
        className,
      )}
    >
      <span
        aria-hidden
        className="grid size-12 place-items-center rounded-full bg-white text-brand shadow-portal-card"
      >
        <UploadIcon className="size-5" />
      </span>
      <span className="text-portal-small font-bold text-portal-ink">
        {uploading ? 'Uploading…' : label}
      </span>
      {hint ? (
        <span id={`${inputId}-hint`} className="text-portal-tiny text-portal-muted">
          {hint} — drop it here, or click to choose a file.
        </span>
      ) : null}
      <input
        ref={inputRef}
        id={inputId}
        type="file"
        accept={accept}
        className="sr-only"
        tabIndex={-1}
        disabled={inert}
        onChange={(event) => {
          take(event.target.files);
          event.target.value = '';
        }}
      />
    </div>
  );
}
